import React, { useCallback, useMemo, useState } from "react";
import {
  AppstoreOutlined,
  ClearOutlined,
  FilterOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import FlightHotelPassengersPage from "../unifiedSearchMockup/FlightHotelPassengersPage";
import FlightHotelPaymentPage from "../unifiedSearchMockup/FlightHotelPaymentPage";
import FlightHotelRoomSelectionPage from "../unifiedSearchMockup/FlightHotelRoomSelectionPage";
import UnifiedOccupancyField from "../unifiedSearchMockup/UnifiedOccupancyField";
import { filterUnifiedFlightHotelCards } from "../unifiedSearchMockup/filterUnifiedFlightHotelCards";
import {
  MOCK_AGENT_QUICK_FILTERS,
  MOCK_BOARD_OPTIONS,
  MOCK_STAR_OPTIONS,
} from "../packagesResultsMockup/mockPackagesData";
import HotelResultCard from "./HotelResultCard";
import {
  UNIFIED_DESTINATIONS,
  cloneSearch,
  createHotelsOnlyDraftSearch,
  matchDemoHotelsSearch,
  summarizeOccupancy,
} from "./mockHotelsSearchFixtures";
import { runHotelsOnlyLiveSearch } from "./runHotelsOnlyLiveSearch";
import "../packagesResultsMockup/PackagesResultsMockupPage.css";
import "../unifiedSearchMockup/UnifiedSearchMockupPage.css";
import "./HotelsSearchMockupPage.css";

function EmptyState({ title, body, hint }) {
  return (
    <div className="usm-empty" role="status">
      <p className="usm-empty__title">{title}</p>
      <p className="usm-empty__body">{body}</p>
      {hint ? <p className="usm-empty__hint">{hint}</p> : null}
    </div>
  );
}

/**
 * Hotels-only search mockup: search → results → room combination → passengers/Pre-Book → book.
 * Reuses Hubwayz room selection / Pre-Book / Book pages from unifiedSearchMockup.
 */
export default function HotelsSearchMockupPage() {
  const [draftSearch, setDraftSearch] = useState(() => createHotelsOnlyDraftSearch());
  const [appliedSearch, setAppliedSearch] = useState(() =>
    cloneSearch(createHotelsOnlyDraftSearch())
  );
  const [viewMode, setViewMode] = useState("list");
  const [sortBy, setSortBy] = useState("price-asc");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hotelQuery, setHotelQuery] = useState("");
  const [selectedBoards, setSelectedBoards] = useState(() => new Set());
  const [selectedStars, setSelectedStars] = useState(() => new Set());
  const [agentQuick, setAgentQuick] = useState(() => new Set());
  const [compareIds, setCompareIds] = useState(() => new Set());
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const [selectedHotelId, setSelectedHotelId] = useState(null);
  const [bookingDraft, setBookingDraft] = useState(null);
  const [flowStep, setFlowStep] = useState("results");
  const [toast, setToast] = useState("");
  const [liveSearching, setLiveSearching] = useState(false);
  const [liveHotelCards, setLiveHotelCards] = useState(null);
  const [liveHubwayzData, setLiveHubwayzData] = useState(null);
  const [liveSearchRequest, setLiveSearchRequest] = useState(null);
  const [liveError, setLiveError] = useState(null);
  const [useLive, setUseLive] = useState(true);

  const demoMatch = useMemo(
    () => matchDemoHotelsSearch(appliedSearch),
    [appliedSearch]
  );
  const occSummary = useMemo(
    () => summarizeOccupancy(appliedSearch.rooms),
    [appliedSearch]
  );

  const hotelCardsRaw =
    useLive && liveHotelCards != null ? liveHotelCards : demoMatch.cards;
  const hubwayzData =
    useLive && liveHubwayzData != null ? liveHubwayzData : demoMatch.hubwayzData;
  const searchRequest =
    useLive && liveSearchRequest != null
      ? liveSearchRequest
      : demoMatch.searchRequest;

  const clearFilters = useCallback(() => {
    setSelectedBoards(new Set());
    setSelectedStars(new Set());
    setAgentQuick(new Set());
    setHotelQuery("");
  }, []);

  const toggleInSet = (setter, id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hotelCards = useMemo(
    () =>
      filterUnifiedFlightHotelCards(hotelCardsRaw, {
        hotelQuery,
        selectedBoards,
        selectedStars,
        selectedNights: new Set(),
        agentQuick,
        baggageFilter: new Set(),
        departWindow: "all",
        seatsFilter: "all",
        sortBy,
      }),
    [
      hotelCardsRaw,
      hotelQuery,
      selectedBoards,
      selectedStars,
      agentQuick,
      sortBy,
    ]
  );

  const activeChips = useMemo(() => {
    const chips = [];
    if (hotelQuery.trim()) {
      chips.push({
        id: "hotel",
        label: `מלון: ${hotelQuery.trim()}`,
        onRemove: () => setHotelQuery(""),
      });
    }
    MOCK_BOARD_OPTIONS.forEach((o) => {
      if (selectedBoards.has(o.id)) {
        chips.push({
          id: `board-${o.id}`,
          label: o.shortLabel || o.label,
          onRemove: () => toggleInSet(setSelectedBoards, o.id),
        });
      }
    });
    MOCK_AGENT_QUICK_FILTERS.forEach((f) => {
      if (agentQuick.has(f.id)) {
        chips.push({
          id: `agent-${f.id}`,
          label: f.label,
          onRemove: () => toggleInSet(setAgentQuick, f.id),
        });
      }
    });
    selectedStars.forEach((s) => {
      chips.push({
        id: `star-${s}`,
        label: `${s}★`,
        onRemove: () => toggleInSet(setSelectedStars, s),
      });
    });
    return chips;
  }, [hotelQuery, selectedBoards, agentQuick, selectedStars]);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2800);
  }, []);

  const patchDraft = (patch) => {
    setDraftSearch((prev) => {
      const next = { ...prev, ...patch };
      if (patch.destinationId) {
        const dest = UNIFIED_DESTINATIONS.find((d) => d.id === patch.destinationId);
        if (dest) {
          next.destinationLabel = dest.label;
          next.packageDestinationId = dest.packageDestinationId;
          next.flightIata = dest.flightIata;
        }
      }
      return next;
    });
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!draftSearch.destinationId) {
      showToast("נא לבחור יעד");
      return;
    }
    const next = cloneSearch(draftSearch);
    if (next.checkIn && next.checkOut) {
      next.dateRangeLabel = `${next.checkIn} → ${next.checkOut}`;
    }
    setAppliedSearch(next);
    setSelectedHotelId(null);
    setFlowStep("results");

    if (!useLive) {
      setLiveHotelCards(null);
      setLiveHubwayzData(null);
      setLiveSearchRequest(null);
      setLiveError(null);
      const demo = matchDemoHotelsSearch(next);
      showToast(`דמו · ${demo.meta.hotelsCount} מלונות · ${demo.meta.occupancyLabel}`);
      return;
    }

    const destMeta = UNIFIED_DESTINATIONS.find((d) => d.id === next.destinationId);
    setLiveSearching(true);
    showToast("מחפש מלונות…");
    try {
      const live = await runHotelsOnlyLiveSearch(next, destMeta);
      setLiveHotelCards(live.hotelCards);
      setLiveHubwayzData(live.hubwayzData);
      setLiveSearchRequest(live.searchRequest);
      setLiveError(live.hotelsError);
      showToast(
        live.hotelsError
          ? `שגיאה: ${live.hotelsError}`
          : `נמצאו ${live.meta.hotelsCount} מלונות`
      );
    } catch (err) {
      setLiveError(err?.message || String(err));
      showToast("שגיאה בחיפוש חי — בדוק BE / credentials");
    } finally {
      setLiveSearching(false);
    }
  };

  const detailLiveContext = useMemo(() => {
    if (!selectedHotelId || !hubwayzData) return null;
    const resultsCard = hotelCardsRaw.find((c) => c.id === selectedHotelId);
    if (!resultsCard) return null;
    return {
      resultsCard,
      flightOffers: [],
      hubwayzData,
      searchRequest,
      appliedSearch,
    };
  }, [selectedHotelId, hubwayzData, hotelCardsRaw, searchRequest, appliedSearch]);

  if (flowStep === "payment" && bookingDraft) {
    return (
      <FlightHotelPaymentPage
        bookingDraft={bookingDraft}
        appliedSearch={appliedSearch}
        hubwayzSearchRequest={searchRequest}
        onBack={() => setFlowStep("passengers")}
        onDone={(meta) => {
          const bid =
            meta?.bookingID ||
            meta?.bookingId ||
            meta?.hotelHold?.bookingId ||
            meta?.hrOrderNumber ||
            "";
          showToast(
            bid
              ? `הזמנה נרשמה · ${bid}`
              : "שריון המלון הושלם"
          );
          setFlowStep("results");
          setSelectedHotelId(null);
          setBookingDraft(null);
        }}
      />
    );
  }

  if (flowStep === "passengers" && bookingDraft) {
    return (
      <FlightHotelPassengersPage
        bookingDraft={bookingDraft}
        appliedSearch={appliedSearch}
        onBack={() => setFlowStep("detail")}
        onExpiredSearch={() => {
          setBookingDraft(null);
          setSelectedHotelId(null);
          setFlowStep("results");
          showToast("קוד ההצעה פג · מריצים חיפוש מחדש");
          void runSearch();
        }}
        onContinueToPayment={(payload) => {
          setBookingDraft(payload);
          setFlowStep("payment");
        }}
      />
    );
  }

  if (flowStep === "detail" && selectedHotelId) {
    return (
      <FlightHotelRoomSelectionPage
        packageId={selectedHotelId}
        liveContext={detailLiveContext}
        onBack={() => {
          setFlowStep("results");
          setSelectedHotelId(null);
          setBookingDraft(null);
        }}
        onContinue={(draft) => {
          setBookingDraft({ ...draft, hotelsOnly: true });
          setFlowStep("passengers");
        }}
      />
    );
  }

  const matched = hotelCardsRaw.length > 0;

  return (
    <div className="usm-page hrm-mock hsm-page" dir="rtl" lang="he">
      <header className="usm-topbar">
        <div className="usm-topbar__brand">
          <span className="usm-topbar__eyebrow">HotelsRiver · Hotels Mockup</span>
          <h1 className="usm-topbar__title">חיפוש מלונות</h1>
        </div>
        <p className="usm-topbar__note">
          תוצאות → בחירת חדרים → נוסעים → הזמנה
        </p>
      </header>

      <form className="usm-search" onSubmit={runSearch} aria-label="סרגל חיפוש מלונות">
        <label className="usm-field usm-field--wide">
          <span className="usm-field__label">יעד</span>
          <select
            className="usm-field__input"
            value={draftSearch.destinationId}
            onChange={(e) => patchDraft({ destinationId: e.target.value })}
            aria-label="יעד"
          >
            {UNIFIED_DESTINATIONS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        <label className="usm-field">
          <span className="usm-field__label">צ׳ק־אין</span>
          <input
            type="date"
            className="usm-field__input"
            value={draftSearch.checkIn}
            onChange={(e) => patchDraft({ checkIn: e.target.value })}
          />
        </label>

        <label className="usm-field">
          <span className="usm-field__label">צ׳ק־אאוט</span>
          <input
            type="date"
            className="usm-field__input"
            value={draftSearch.checkOut}
            onChange={(e) => patchDraft({ checkOut: e.target.value })}
          />
        </label>

        <div className="usm-field usm-field--occ">
          <span className="usm-field__label">אורחים וחדרים</span>
          <UnifiedOccupancyField
            rooms={draftSearch.rooms}
            onChange={(rooms) => patchDraft({ rooms })}
          />
        </div>

        <button type="submit" className="usm-search__submit">
          <SearchOutlined aria-hidden />
          חפש מלונות
        </button>
      </form>

      <div className="usm-applied" aria-live="polite">
        <span>
          חיפוש פעיל: {appliedSearch.destinationLabel} ·{" "}
          {appliedSearch.dateRangeLabel ||
            `${appliedSearch.checkIn} → ${appliedSearch.checkOut}`}{" "}
          · {occSummary.occupancyLabel}
        </span>
        <label className="usm-applied__live">
          <input
            type="checkbox"
            checked={useLive}
            onChange={(e) => setUseLive(e.target.checked)}
          />
          חיפוש חי
        </label>
        <span className="usm-applied__demo">
          {liveSearching
            ? "טוען…"
            : useLive && liveHotelCards != null
              ? "מקור: מלונות"
              : "fixtures דמו (שילובי 1 / 3 חדרים)"}
        </span>
      </div>

      <div className="usm-results" role="region" aria-label="תוצאות מלונות">
        <section className="usm-packages">
          <div className="usm-fh-filters hrm-sticky usm-fh-filters-sticky">
            <div className="hrm-tools" aria-label="מיון וסינון תוצאות">
              <span className="hrm-tools__count">
                {liveSearching
                  ? "טוען…"
                  : matched
                    ? `${hotelCards.length} תוצאות`
                    : "0 תוצאות"}
                {appliedSearch.destinationLabel
                  ? ` ב${appliedSearch.destinationLabel}`
                  : ""}
                {activeChips.length > 0 && matched
                  ? ` · מתוך ${hotelCardsRaw.length}`
                  : ""}
              </span>

              {activeChips.length > 0 ? (
                <div className="hrm-tools__chips">
                  {activeChips.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className="hrm-chip"
                      onClick={chip.onRemove}
                    >
                      {chip.label}
                      <ClearOutlined />
                    </button>
                  ))}
                  <button type="button" className="hrm-link" onClick={clearFilters}>
                    ניקוי הכול
                  </button>
                </div>
              ) : null}

              <div className="hrm-tools__end">
                <div className="hrm-view-toggle" role="group" aria-label="תצוגה">
                  <button
                    type="button"
                    className={viewMode === "list" ? "is-active" : ""}
                    aria-pressed={viewMode === "list"}
                    onClick={() => setViewMode("list")}
                    title="תצוגת רשימה"
                  >
                    <UnorderedListOutlined />
                  </button>
                  <button
                    type="button"
                    className={viewMode === "grid" ? "is-active" : ""}
                    aria-pressed={viewMode === "grid"}
                    onClick={() => setViewMode("grid")}
                    title="תצוגת גריד"
                  >
                    <AppstoreOutlined />
                  </button>
                </div>
                <label className="hrm-sort">
                  <span className="visually-hidden">מיון</span>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="price-asc">מחיר: מהזול ליקר</option>
                    <option value="price-desc">מחיר: מהיקר לזול</option>
                    <option value="stars">דירוג מלון</option>
                  </select>
                </label>
                <button
                  type="button"
                  className={`hrm-tools__filter-btn${filtersOpen ? " is-open" : ""}`}
                  aria-expanded={filtersOpen}
                  aria-controls="hsm-filters-panel"
                  onClick={() => setFiltersOpen((v) => !v)}
                >
                  <FilterOutlined />
                  סינון
                  {activeChips.length > 0 ? (
                    <span className="hrm-tools__filter-count">{activeChips.length}</span>
                  ) : null}
                </button>
              </div>
            </div>

            <div
              id="hsm-filters-panel"
              className={`hrm-filters-panel${filtersOpen ? " is-open" : ""}`}
              aria-hidden={!filtersOpen}
            >
              <div className="hrm-filters-panel__grid">
                <label className="hrm-filters-panel__field">
                  <span>חיפוש מלון</span>
                  <input
                    type="search"
                    value={hotelQuery}
                    onChange={(e) => setHotelQuery(e.target.value)}
                    placeholder="שם מלון…"
                  />
                </label>

                <fieldset className="hrm-filters-panel__group">
                  <legend>בסיס אירוח</legend>
                  {MOCK_BOARD_OPTIONS.map((o) => (
                    <label key={o.id}>
                      <input
                        type="checkbox"
                        checked={selectedBoards.has(o.id)}
                        onChange={() => toggleInSet(setSelectedBoards, o.id)}
                      />
                      {o.shortLabel || o.label}
                    </label>
                  ))}
                </fieldset>

                <fieldset className="hrm-filters-panel__group">
                  <legend>לסוכן</legend>
                  {MOCK_AGENT_QUICK_FILTERS.map((f) => (
                    <label key={f.id}>
                      <input
                        type="checkbox"
                        checked={agentQuick.has(f.id)}
                        onChange={() => toggleInSet(setAgentQuick, f.id)}
                      />
                      {f.label}
                    </label>
                  ))}
                </fieldset>

                <fieldset className="hrm-filters-panel__group">
                  <legend>דירוג</legend>
                  {MOCK_STAR_OPTIONS.map((o) => (
                    <label key={o.id}>
                      <input
                        type="checkbox"
                        checked={selectedStars.has(o.id)}
                        onChange={() => toggleInSet(setSelectedStars, o.id)}
                      />
                      {o.label}
                    </label>
                  ))}
                </fieldset>
              </div>
              {activeChips.length > 0 ? (
                <div className="hrm-filters-panel__foot">
                  <button type="button" className="hrm-link" onClick={clearFilters}>
                    ניקוי הכול
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {liveSearching ? (
            <EmptyState
              title="מחפש מלונות…"
              body="חיפוש מלון בלבד — ללא טיסות LDS. המחירים מהספק בלבד."
            />
          ) : !matched ? (
            <EmptyState
              title="אין מלונות ליעד הזה"
              body={
                liveError ||
                "לא התקבלו תוצאות. נסו תאריכים אחרים, או כבו «חי» לצפייה ב־fixtures דמו."
              }
              hint={
                occSummary.rooms > 1
                  ? `חיפשתם ${occSummary.rooms} חדרים — מוצגים רק שילובים מלאים מהספק.`
                  : null
              }
            />
          ) : hotelCards.length === 0 ? (
            <EmptyState
              title="אין מלונות שתואמים את הסינון"
              body="נסו לנקות סינונים או לשנות חיפוש."
            />
          ) : (
            <div
              className={
                viewMode === "grid" ? "hrm-results__grid" : "hrm-results__list"
              }
            >
              {hotelCards.map((hotel) => (
                <HotelResultCard
                  key={hotel.id}
                  hotel={hotel}
                  viewMode={viewMode}
                  compared={compareIds.has(hotel.id)}
                  favorited={favoriteIds.has(hotel.id)}
                  onToggleCompare={(id) => toggleInSet(setCompareIds, id)}
                  onToggleFavorite={(id) => toggleInSet(setFavoriteIds, id)}
                  onSelect={(h) => {
                    setSelectedHotelId(h.id);
                    setFlowStep("detail");
                  }}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {toast ? (
        <div className="usm-toast hrm-toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
